import {
  useIsMobile,
  WSButton,
  WSCheckboxToggle,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { IUserCreateRequest } from "@wingspanhq/users";
import {
  INewUser,
  UserAccountType
} from "@wingspanhq/users/dist/lib/interfaces/newUser";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import {
  useCreateSession,
  useCreateUser,
  useIdentifyAuthentication,
  useSignInWithGoogle,
  useUpdateUserProfile
} from "../../../query/users/mutations";
import { ISignInWithGoogleRequest } from "../../../services/users";
import { useSetWSStore, useWSStore } from "../../../store";
import { WS_LINKS } from "../../../types/wsLinks";
import { track } from "../../../utils/analytics";
import {
  validatorEmail,
  validatorSignInPassword
} from "../../../utils/validators";
import { useWSAnalytics } from "../../../utils/WSAnalytics";
import { Divider } from "../Divider/Divider";
import { GoogleAuthButton } from "../GoogleAuthButton/GoogleAuthButton";
import styles from "./SignUpForm.module.scss";

interface SignUpFormData {
  email: string;
  password: string;
}

export interface SignUpFormProps {
  defaultValues?: Partial<SignUpFormData>;
  title?: string;
  description?: string;
  buttonText?: string;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  defaultValues = {},
  title = "Sign up",
  description,
  buttonText = "Try Wingspan free"
}) => {
  const setStore = useSetWSStore();
  const store = useWSStore();

  const history = useHistory();
  const isMobile = useIsMobile();
  const [signUpWithGoogleError, setSignUpWithGoogleError] = useState("");
  const [captchaLoadError, setCaptchaLoadError] = useState(false);
  const [ssoData, setSsoData] = useState({
    url: "",
    domain: ""
  });

  const [updateUser] = useUpdateUserProfile();
  const [createUser, createUserMeta] = useCreateUser();
  const [createSession, createSessionMeta] = useCreateSession();
  const [signUpWithGoogle] = useSignInWithGoogle();
  const [identifyAuth, identifyAuthMeta] = useIdentifyAuthentication();

  const analytics = useWSAnalytics();

  const [prefilledEmail, setPrefilledEmail] = useState("");
  const [oldEmail, setOldEmail] = useState("");

  const handleDefaultValues = () => {
    // if a prefilled email present, we're going to want to try to use this for the user creation first
    if (defaultValues.email) {
      setPrefilledEmail(defaultValues.email);
      setOldEmail(defaultValues.email);
    }
  };

  useEffect(handleDefaultValues, []);

  useEffect(() => {
    if (createSessionMeta.error?.message === "Captcha loading error") {
      setCaptchaLoadError(true);
    }
  }, [createSessionMeta.error?.message]);

  const onGoogleLoginSuccess = (request: ISignInWithGoogleRequest) => {
    signUpWithGoogle(request, {
      onSuccess: () => {
        setStore({ signUpMethod: "Google" });
        analytics.track.signUp();
        history.replace("/member/onboarding/customizing");
      }
    });
  };
  const onGoogleLoginFailure = (errorRes: any) => {
    setSignUpWithGoogleError(errorRes.error);
  };

  const onFormSubmit = async (data: SignUpFormData) => {
    createUserMeta.reset();
    createSessionMeta.reset();
    setCaptchaLoadError(false);

    // if a prefilled email was used, try to perform the sign up with this email address
    let originalEmail = "";
    if (prefilledEmail) {
      originalEmail = data.email;
      data.email = prefilledEmail;
    }

    const request: IUserCreateRequest = {
      ...data,
      notificationSettings: {
        reviewNotifications: true,
        newsletters: true
      },
      ...(store.isEmployerSignUp
        ? { settings: { userAccountType: UserAccountType.enterprise } }
        : {})
    };

    try {
      const identifyResult = await identifyAuth(
        { email: request.email },
        {
          throwOnError: true
        }
      );

      if (identifyResult?.authenticationStrategy === "SingleSignOn") {
        setSsoData({
          url: identifyResult?.authenticationUrl || "",
          domain: request.email.split("@").pop() || ""
        });
        return;
      } else {
        setSsoData({
          url: "",
          domain: ""
        });
      }
    } catch {
      setSsoData({
        url: "",
        domain: ""
      });
    }

    await createUser(request, {
      onSuccess: async (user: INewUser) =>
        await createSession(
          {
            ...data
          },
          {
            onSuccess: async () => {
              if (originalEmail !== "") {
                // we were doing the switcharoo, so lets update the user's email immediately
                await updateUser(
                  {
                    email: originalEmail
                  },
                  {
                    onSuccess: () => {
                      // if user wants to sign-up with another email, allow it but track old & new emails
                      if (
                        defaultValues.email &&
                        defaultValues.email !== originalEmail
                      ) {
                        track("Collaborator Email Updated", {
                          originalEmail: defaultValues.email,
                          newEmail: originalEmail
                        });
                      }
                    }
                  }
                );
              }
              setStore({ signUpMethod: "Email" });
              analytics.track.signUp();
              history.replace("/member/onboarding/customizing");
            }
          }
        ),
      onError: async error => {
        if (
          originalEmail !== "" &&
          error.response?.data?.error === "User for email already exists"
        ) {
          // if we're doing the email switcharoo and the email we switched in already exists, log in as that user
          await createSession(
            {
              ...data
            },
            {
              onSuccess: async () => {
                history.replace("/member/dashboard");
              },
              onError: async error => {
                if (
                  error.response?.data.error ===
                  "Sorry! The password you entered does not match what we have on file."
                ) {
                  // if the login failed, we don't wanna keep screwing round, just create the user normally
                  request.email = originalEmail;
                  data.email = originalEmail;
                  setPrefilledEmail("");
                  await createUser(request, {
                    onSuccess: async (user: INewUser) =>
                      await createSession(
                        {
                          ...data
                        },
                        {
                          onSuccess: async () => {
                            // if user wants to sign-up with another email, allow it but track old & new emails
                            if (
                              defaultValues.email &&
                              defaultValues.email !== originalEmail
                            ) {
                              track("Collaborator Email Updated", {
                                originalEmail: defaultValues.email,
                                newEmail: originalEmail
                              });
                            }
                            setStore({ signUpMethod: "Email" });
                            analytics.track.signUp();
                            history.replace("/member/onboarding/customizing");
                          }
                        }
                      )
                  });
                }
              }
            }
          );
        }
      }
    });
  };

  return (
    <WSFlexBox.CenterX
      ml={isMobile ? "NONE" : "XL"}
      direction="column"
      px={isMobile ? "M" : "XL"}
      py="M"
      className={styles.formContainer}
      alignItems="stretch"
    >
      <WSText.Heading2 mb="XS">{title}</WSText.Heading2>
      {store.is1099SignUp && description ? (
        <WSText.Paragraph mb="XS">{description}</WSText.Paragraph>
      ) : null}
      <WSText.Paragraph color="grey800" mb="L">
        Already a member?{" "}
        <WSButton.Link inline onClick={() => history.push("/member/sign-in")}>
          Sign in.
        </WSButton.Link>
      </WSText.Paragraph>
      <GoogleAuthButton
        onLoginSuccess={onGoogleLoginSuccess}
        onLoginFailure={onGoogleLoginFailure}
      >
        Sign up with Google
      </GoogleAuthButton>
      <Divider or />
      <WSForm
        defaultValues={{
          email: "",
          password: "",
          remember: true,
          ...defaultValues
        }}
        validationSchema={Yup.object().shape({
          email: validatorEmail.required("Email is required"),
          password: validatorSignInPassword,
          remember: Yup.boolean()
        })}
        onSubmit={onFormSubmit}
      >
        <WSForm.Field
          mb="L"
          name="email"
          label="Email address"
          component={WSTextInput}
          hidden={!!oldEmail}
          componentProps={{
            type: "email",
            className: styles.input
          }}
        />

        {oldEmail ? (
          <WSFlexBox.CenterY
            py="S"
            mb="M"
            className={styles.emailPreview}
            wrap="nowrap"
          >
            <WSFlexBox.Center mr="S" className={styles.userIcon}>
              <WSIcon name="personalInfo" color="grey500" />
            </WSFlexBox.Center>

            <WSElement className={styles.userDetails}>
              <WSText.Paragraph singleLine>{oldEmail}</WSText.Paragraph>
              <WSButton.Link
                type="button"
                onClick={() => {
                  createUserMeta.reset();
                  createSessionMeta.reset();
                  setOldEmail("");
                }}
              >
                Not you?
              </WSButton.Link>
            </WSElement>
          </WSFlexBox.CenterY>
        ) : null}
        <WSForm.Field
          mb="L"
          name="password"
          label="Password"
          component={WSTextInput}
          componentProps={{
            type: "password",
            className: styles.input
          }}
        />

        {ssoData.url ? (
          <WSText.Label color="red500" mb="M">
            Your domain <b>{ssoData.domain}</b> has SSO enabled.{" "}
            <a href={ssoData.url} target="_self">
              <b>Sign in with SSO →</b>
            </a>
          </WSText.Label>
        ) : null}

        <WSForm.Field
          mb="M"
          name="remember"
          component={WSCheckboxToggle}
          componentProps={{ label: "Stay signed in" }}
        />

        <WSErrorMessage
          my="M"
          contextKey="SignUp"
          forceShowApiErrors
          error={
            createUserMeta.error ||
            createSessionMeta.error ||
            signUpWithGoogleError
          }
        />

        <WSButton
          name="signUp"
          fullWidth
          type="submit"
          className={styles.tryWSBtn}
          loading={createSessionMeta.isLoading || createUserMeta.isLoading}
        >
          {buttonText}
        </WSButton>

        {captchaLoadError && (
          <WSText color="coral" mb="M">
            Sorry, something went wrong.{" "}
            <WSButton.Link
              inline
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload and try again
            </WSButton.Link>
          </WSText>
        )}
      </WSForm>
      <WSText.ParagraphSmall mt="XL" color="grey700">
        By proceeding you agree to Wingspan's{" "}
        <a
          href={WS_LINKS.termsOfService}
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>{" "}
        {"& "}
        <a
          href={WS_LINKS.privacyPolicy}
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        . This site is protected by reCAPTCHA and the Google{" "}
        <a
          href={WS_LINKS.googlePrivacyPolicy}
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href={WS_LINKS.googleTermOfService}
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{" "}
        apply.
      </WSText.ParagraphSmall>
    </WSFlexBox.CenterX>
  );
};
